import { create } from "zustand";
import { JobType, MoneyUnit } from "../shared/types/enum";
import { BusinessAddress } from "../types/business";
import { JobCreate } from "../types/job";
import { PostType, SkillSelect } from "../types/metadata/metadata";

interface EditJobState {
  job: JobCreate;
}

interface EditJobActions {
  setJob: (job: JobCreate) => void;
  setPostType: (postType: PostType) => void;
  setJobTitle: (jobTitle: string) => void;
  setCategoryId: (categoryId: string) => void;
  setSkills: (skills: SkillSelect[]) => void;
  setLevelId: (levelId: string) => void;
  setJobType: (jobType: JobType) => void;
  setDegreeId: (degreeId: string) => void;
  setMinYearExp: (minYearExperience: number) => void;
  setMaxYearExp: (maxYearExperience: number) => void;
  setMinSalary: (minSalary: number) => void;
  setMaxSalary: (maxSalary: number) => void;
  setMoneyUnit: (moneyUnit: MoneyUnit) => void;
  setIsSalaryNegotiable: (isNegotiable: boolean) => void;
  setJobDescription: (jobDescription: string) => void;
  setJobRequirement: (jobRequirement: string) => void;
  setJobResponsibility: (jobResponsibility: string) => void;
  setJobBenefit: (jobBenefit: string) => void;
  setNoteForCandidate: (jobNote: string) => void;
  setJobAddress: (address: BusinessAddress) => void;
  reset: () => void;
}

export type EditJobStore = EditJobState & EditJobActions;

const initialState: EditJobState = {
  job: {
    skills: [],
    jobType: JobType.fullTimeOffice,
    isNegotiable: false,
    moneyUnit: MoneyUnit.vnd,
    address: undefined,
  },
};

export const useEditJobStore = create<EditJobStore>((set) => ({
  ...initialState,
  setJob: (job) => set({ job }),
  setPostType: (postType) =>
    set((state) => ({ job: { ...state.job, postType } })),
  setJobTitle: (jobTitle) =>
    set((state) => ({ job: { ...state.job, jobTitle } })),
  setCategoryId: (categoryId) =>
    set((state) => ({ job: { ...state.job, categoryId } })),
  setSkills: (skills) => set((state) => ({ job: { ...state.job, skills } })),
  setLevelId: (levelId) => set((state) => ({ job: { ...state.job, levelId } })),
  setJobType: (jobType) => set((state) => ({ job: { ...state.job, jobType } })),
  setDegreeId: (degreeId) =>
    set((state) => ({ job: { ...state.job, degreeId } })),
  setMinYearExp: (minYearExperience) =>
    set((state) => ({ job: { ...state.job, minYearExperience } })),
  setMaxYearExp: (maxYearExperience) =>
    set((state) => ({ job: { ...state.job, maxYearExperience } })),
  setMinSalary: (minSalary) =>
    set((state) => ({ job: { ...state.job, minSalary } })),
  setMaxSalary: (maxSalary) =>
    set((state) => ({ job: { ...state.job, maxSalary } })),
  setMoneyUnit: (moneyUnit) =>
    set((state) => ({ job: { ...state.job, moneyUnit } })),
  setIsSalaryNegotiable: (isNegotiable) =>
    set((state) => ({ job: { ...state.job, isNegotiable } })),
  setJobDescription: (jobDescription) =>
    set((state) => ({ job: { ...state.job, jobDescription } })),
  setJobRequirement: (jobRequirement) =>
    set((state) => ({ job: { ...state.job, jobRequirement } })),
  setJobResponsibility: (jobResponsibility) =>
    set((state) => ({ job: { ...state.job, jobResponsibility } })),
  setJobBenefit: (jobBenefit) =>
    set((state) => ({ job: { ...state.job, jobBenefit } })),
  setNoteForCandidate: (jobNote) =>
    set((state) => ({ job: { ...state.job, jobNote } })),
  setJobAddress: (address: BusinessAddress) =>
    set((state) => ({
      job: {
        ...state.job,
        country: address.countryCode,
        city: address.provinceName,
        province: address.provinceName,
        district: address.districtName || "",
        ward: address.wardName,
        street: address.streetName,
        address: [
          address.streetName,
          address.wardName,
          address.districtName,
          address.provinceName,
        ]
          .filter(Boolean)
          .join(", "),
      },
    })),
  reset: () => set(initialState),
}));
